import { Link } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import { t } from '../../i18n';
import { Icon } from '../icons';
import { cn } from '../lib/utils';
import type { NavItemDef } from '../types';

type Variant = 'bottom' | 'sidebar';

type NavItemProps = {
  item: NavItemDef;
  active?: boolean;
  variant: Variant;
  /** The More menu closes its drawer when an item is chosen. */
  onClick?: () => void;
};

// A thumb-sized cell with the icon over the label in the bottom bar; a row in the sidebar (design system §1, §4).
export function navItemClassName(variant: Variant, active: boolean) {
  return cn(
    'relative flex min-h-(--target-min) items-center text-muted focus-ring hover:text-text',
    variant === 'bottom'
      ? 'min-w-(--target-min) flex-1 flex-col justify-center gap-1 px-1 text-xs'
      : 'gap-3 rounded-md px-3 text-sm',
    active && 'bg-accent-muted text-text',
  );
}

// Active is never shown by colour alone: a gold bar (top edge in the bottom bar, start edge in the sidebar) and a
// heavier label (design system §2).
export function NavItemContent({
  icon,
  label,
  variant,
  active,
}: {
  icon: LucideIcon;
  label: string;
  variant: Variant;
  active: boolean;
}) {
  return (
    <>
      {active && (
        <span
          className={cn(
            'absolute bg-accent',
            variant === 'bottom' ? 'inset-x-0 top-0 h-1' : 'inset-y-0 inset-s-0 w-1',
          )}
        />
      )}
      <Icon icon={icon} />
      {/* A fifth of 360 px at 200 % text: the label wraps, even inside a word, never cut (design system §1). */}
      <span
        className={cn(
          variant === 'bottom' && 'text-center wrap-anywhere',
          active && 'font-semibold',
        )}
      >
        {label}
      </span>
    </>
  );
}

export function NavItem({ item, active = false, variant, onClick }: NavItemProps) {
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      data-active={active || undefined}
      className={navItemClassName(variant, active)}
      onClick={onClick}
    >
      <NavItemContent icon={item.icon} label={item.label} variant={variant} active={active} />
      {item.badge ? (
        <>
          {/* The count is read once, through the sentence after it. */}
          <span
            aria-hidden
            className={cn(
              'rounded-full bg-text px-1 text-xs font-semibold text-background',
              variant === 'bottom' ? 'absolute top-1 inset-s-1/2 ms-2' : 'ms-auto',
            )}
          >
            {item.badge}
          </span>
          <span className="sr-only">
            {item.badgeLabel ?? t('components.NavItem.badge', { count: item.badge })}
          </span>
        </>
      ) : null}
    </Link>
  );
}
